import type { CollectionPagination } from 'api/market-view-api';

export interface ParsePaginationHeadersOptions {
  /** Fallback when `X-Page-Count` is missing. */
  pageIndex?: number;
  /** Fallback when `X-Page-Size` is missing. */
  pageSize?: number;
}

const readHeader = (
  headers: Record<string, unknown> | undefined,
  name: string
): string | undefined => {
  if (!headers) return undefined;

  const value = headers[name] ?? headers[name.toLowerCase()];

  if (Array.isArray(value))
    return value[0] != null ? String(value[0]) : undefined;
  if (value == null) return undefined;

  return String(value);
};

/**
 * Maps Spring `PaginationUtil` response headers to the FE `CollectionPagination`
 * shape used by list tables.
 *
 * Headers: `X-Total-Count`, `X-Page-Size`, `X-Page-Count` (0-based page index).
 */
export const parsePaginationHeaders = (
  headers: Record<string, unknown> | undefined,
  {
    pageIndex = 0,
    pageSize: fallbackPageSize = 10,
  }: ParsePaginationHeadersOptions = {}
): CollectionPagination => {
  const totalCount = Number.parseInt(
    readHeader(headers, 'x-total-count') ?? '0',
    10
  );
  const pageSize = Number.parseInt(
    readHeader(headers, 'x-page-size') ?? String(fallbackPageSize),
    10
  );
  const page = Number.parseInt(
    readHeader(headers, 'x-page-count') ?? String(pageIndex),
    10
  );
  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallbackPageSize;
  const safeTotal = Number.isFinite(totalCount) ? totalCount : 0;

  return {
    page: Number.isFinite(page) ? page : pageIndex,
    pageSize: safePageSize,
    pageCount: Math.ceil(safeTotal / safePageSize) || 0,
    totalRecords: safeTotal,
  };
};
